// NotesTab.tsx  (single shared version, no more Contact/Company duplicates)
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Plus } from 'lucide-react'
import { FormikProps } from 'formik'
import toast from 'react-hot-toast'
import { useContactOwners } from 'service/crm'
import { useAppSelector } from 'store'
import { selectAuthUser } from 'store/slices/auth/auth.selector'
import { Note, NoteFormValues } from 'types/crm/notes'
import CreateNote from './CreateNote'
import NotesList from './NoteList'
import { CrmCommentHooks } from 'types/crm/comments'
import WarningDialog from 'components/common/WarningDialog'
import { useRecordAccess } from 'context/crm/RecordAccessContext'
import DeleteModal, { CascadeItem } from 'components/Modals/CrmDeleteModal'


export interface NoteQueryResult {
    notes: any[]
    isLoading: boolean
    hasMore: boolean
    isFetchingMore: boolean
    onLoadMore: () => void
    refetch: () => void
}

interface NotesTabProps {
    isActive: boolean
    openTrigger?: number
    entityUuid: string
    useNotesQuery: (entityUuid: string) => NoteQueryResult
    useCreateNote: () => { mutateAsync: (payload: any) => Promise<any>; isPending: boolean }
    buildCreatePayload: (entityUuid: string, values: NoteFormValues,) => any
    /** Mutation hook for editing a note. Same shape as useCreateNote. */
    useUpdateNote: () => { mutateAsync: (payload: any) => Promise<any>; isPending: boolean }
    /** Builds the update payload from the entity uuid, the note being edited, and the new form values. */
    buildUpdatePayload: (entityUuid: string, noteUuid: string, values: NoteFormValues) => any
    /** Mutation hook for deleting a note. */
    useDeleteNote: () => { mutateAsync: (payload: any) => Promise<any>; isPending: boolean }
    /** Builds the delete payload from the entity uuid and note uuid. Lets each entity
     *  (contact/deal/company) decide its own key name — contactUuid, dealUuid, companyUuid —
     *  instead of this shared component hardcoding one. */
    buildDeletePayload: (entityUuid: string, noteUuid: string) => any
    commentHooks: CrmCommentHooks
}

const NotesTab = ({
    isActive,
    openTrigger,
    entityUuid,
    useNotesQuery,
    useCreateNote,
    buildCreatePayload,
    useUpdateNote,
    buildUpdatePayload,
    useDeleteNote,
    buildDeletePayload,
    commentHooks,
}: NotesTabProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const [editingNote, setEditingNote] = useState<Note | null>(null)
    // Note pending delete confirmation via WarningDialog. Null = dialog closed.
    const [deleteTarget, setDeleteTarget] = useState<Note | null>(null)
    const formikRef = useRef<FormikProps<NoteFormValues>>(null)

    /**
     * RBAC: `canAddNote` gates creating, `canEdit`/`canDelete` gate mutating an
     * existing note. NotesList stays permission-unaware — it already hides any
     * affordance whose callback is undefined, so we simply don't pass one.
     * Reading the list and commenting are never gated (sections 4, 14, 15).
     */
    const { canAddNote, canEdit, canDelete } = useRecordAccess()
    const canUseForm = canAddNote || canEdit

    const authUser = useAppSelector(selectAuthUser)
    const { data: owners = [] } = useContactOwners(authUser?.orgUuid as string)
    const { notes, isLoading, hasMore, isFetchingMore, onLoadMore, refetch } = useNotesQuery(entityUuid)
    const createNoteMutation = useCreateNote()
    const updateNoteMutation = useUpdateNote()
    const deleteNoteMutation = useDeleteNote()

    const closeForm = () => {
        setIsOpen(false)
        setEditingNote(null)
    }

    const openCreateForm = () => {
        if (!canAddNote) return
        setEditingNote(null)
        setIsOpen(true)
    }

    useEffect(() => {
        if (!openTrigger) return
        if (!canAddNote) return
        if (isOpen) formikRef.current?.resetForm()
        else {
            setEditingNote(null)
            setIsOpen(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openTrigger])

    useEffect(() => {
        if (!isActive && isOpen) {
            formikRef.current?.resetForm()
            closeForm()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive])

    // Section 23: access can disappear mid-session (Task/Note reassigned, Owner
    // changed). Collapse any open form rather than leaving a dead one on screen.
    useEffect(() => {
        if (!canUseForm) {
            closeForm()
            setDeleteTarget(null)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canUseForm])

    const handleSubmit = async (
        values: NoteFormValues,
        { resetForm }: { resetForm: () => void },
    ) => {

        if (!authUser?.appUserUuid || !entityUuid) {
            toast.error('Missing authentication context')
            return
        }
        if (editingNote ? !canEdit : !canAddNote) {
            toast.error('You do not have permission to modify notes on this record.')
            closeForm()
            return
        }
        const note = values.note?.trim()
        if (!note) {
            toast.error('Note cannot be empty')
            return
        }
        try {
            if (editingNote) {
                await updateNoteMutation.mutateAsync(
                    buildUpdatePayload(entityUuid, editingNote.uuid, values),
                )
                toast.success('Note updated')
            } else {
                await createNoteMutation.mutateAsync(
                    buildCreatePayload(entityUuid, values),
                )
                toast.success('Note added')
            }
            resetForm()
            closeForm()
        } catch (err: any) {
            toast.error(
                err?.message ??
                (editingNote
                    ? 'Failed to update note. Please try again.'
                    : 'Something went wrong. Please try again.')
            )
        }
    }

    const openEditForm = (note: Note) => {
        if (!canEdit) return
        setEditingNote(note)
        setIsOpen(true)
    }

    // Opens the WarningDialog rather than deleting immediately.
    const requestDeleteNote = (noteUuid: string) => {
        if (!canDelete) return
        const note = notes?.find((n) => n.uuid === noteUuid) ?? null
        setDeleteTarget(note ?? ({ uuid: noteUuid } as Note))
    }

    const handleCancelDelete = () => setDeleteTarget(null)

    const handleConfirmDelete = async () => {
        if (!deleteTarget || !entityUuid) return
        if (!canDelete) {
            toast.error('You do not have permission to delete notes on this record.')
            setDeleteTarget(null)
            return
        }

        try {
            await deleteNoteMutation.mutateAsync(
                buildDeletePayload(entityUuid, deleteTarget.uuid),
            )
            toast.success('Note deleted')
            // If the note being edited was just deleted, close the form too.
            if (editingNote?.uuid === deleteTarget.uuid) closeForm()
        } catch (err: any) {
            toast.error(err?.message ?? 'Failed to delete note. Please try again.')
        } finally {
            setDeleteTarget(null)
        }
    }

    /** The note plus its comment thread. Comment row is omitted when there are none. */
    const deleteCascade = useMemo<CascadeItem[]>(() => {
        if (!deleteTarget) return []

        const { commentsCount, comments } = deleteTarget as Note &
            Partial<{ commentsCount: number; comments: unknown[] }>
        const commentTotal = commentsCount ?? comments?.length

        return [
            { count: 1, label: 'Note' },
            ...(commentTotal ? [{ count: commentTotal, label: 'Comment' }] : []),
        ]
    }, [deleteTarget])

    if (!isActive) return null

    return (
        <div className="space-y-4">
            {canAddNote && !isOpen && notes?.length > 0 && (
                <button
                    onClick={openCreateForm}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-primary-subtle text-neutral-faded text-sm font-medium hover:bg-primary-surface hover:text-neutral transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Add a note
                </button>
            )}
            {isOpen && entityUuid && canUseForm && (
                <CreateNote
                    entityUuid={entityUuid}
                    owners={owners}
                    note={editingNote}
                    formikRef={formikRef}
                    onCancel={closeForm}
                    onSubmit={handleSubmit}
                    isSubmitting={createNoteMutation.isPending || updateNoteMutation.isPending}
                />
            )}
            <NotesList
                notes={notes}
                isLoading={isLoading}
                hasMore={hasMore}
                isFetchingMore={isFetchingMore}
                onLoadMore={onLoadMore}
                // Omitting these hides the empty-state CTA and the per-note
                // action menu — NotesList already treats undefined as "hide".
                onAddNote={canAddNote ? openCreateForm : undefined}
                onEditNote={canEdit ? openEditForm : undefined}
                onDeleteNote={canDelete ? requestDeleteNote : undefined}
                isFormOpen={isOpen}
                owners={owners}
                parentUuid={entityUuid}
                commentHooks={commentHooks}
                onCommentAdded={refetch}
            />

            <DeleteModal
                isOpen={!!deleteTarget}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                isDeleting={deleteNoteMutation.isPending}
                entityLabel="Note"
                cascade={deleteCascade}
            />
        </div>
    )
}

export default NotesTab